import * as React from "react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const TAGS = ["Great work", "Team player", "Above and beyond", "Kind moment", "Sharp thinking"];

/**
 * Create a recognition post. Server mirrors it onto the recipient's HR
 * record timeline via `hr.recognition` event.
 */
export default function RecognitionComposerDialog({ open, onOpenChange, companyId, conversation }) {
  const [members, setMembers] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [recipient, setRecipient] = React.useState(null);
  const [tag, setTag] = React.useState(TAGS[0]);
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) { setQ(""); setRecipient(null); setTag(TAGS[0]); setNote(""); }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}/search/members`, {
          params: { q, limit: 8 },
        });
        setMembers(data.members || []);
      } catch {}
    }, 100);
    return () => clearTimeout(h);
  }, [q, companyId, open]);

  async function submit() {
    if (!recipient) { toast.error("Pick a teammate to recognise."); return; }
    if (!note.trim()) { toast.error("Add a short note."); return; }
    setBusy(true);
    try {
      await api.post(
        `/companies/${companyId}/comms/conversations/${conversation.id}/recognitions`,
        { recipient_user_id: recipient.id, tag, note: note.trim() },
      );
      toast.success("Recognition posted");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't post");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="recognition-composer-dialog">
        <DialogHeader>
          <DialogTitle>Recognise a teammate</DialogTitle>
          <DialogDescription>
            Shout-outs also land on their HR record timeline.
          </DialogDescription>
        </DialogHeader>
        <input
          type="text" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search a teammate…"
          className="field-input h-9 text-[13px]"
          data-testid="recognition-composer-search"
        />
        <div className="mt-1.5 max-h-40 overflow-y-auto rounded border border-brand-rule" data-testid="recognition-composer-members">
          {members.filter((m) => m.id !== recipient?.id).map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setRecipient(m)}
              className={`w-full text-left px-3 py-1.5 text-[12.5px] hover:bg-brand-hush ${recipient?.id === m.id ? "bg-accent" : ""}`}
              data-testid={`recognition-composer-member-${m.id}`}
            >
              <div className="font-medium text-brand-ink">{m.name}</div>
              <div className="text-brand-muted text-[10.5px]">{m.email}</div>
            </button>
          ))}
        </div>
        {recipient && (
          <div className="mt-2 flex items-center gap-2 text-[12px] text-brand-muted">
            Recognising <strong className="text-brand-ink">{recipient.name}</strong>
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {TAGS.map((t) => (
            <button
              key={t} type="button"
              onClick={() => setTag(t)}
              className={`chip mono-num text-[11px] ${tag === t ? "chip-violet" : ""}`}
              data-testid={`recognition-composer-tag-${t.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {t}
            </button>
          ))}
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What did they do? (a specific example lands hardest)"
          className="field-input mt-3 min-h-[72px] text-[13px]"
          maxLength={1000}
          data-testid="recognition-composer-note"
        />
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" className="btn-ghost h-8" onClick={() => onOpenChange(false)}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="btn-violet h-8"
            data-testid="recognition-composer-submit"
          >
            {busy ? "…" : "Post recognition"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
